import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import useBusinessResource from "./useBusinessResource";
import { invalidateBusinessResources } from "../utils/businessResourceFreshness";

// Each option keeps its own baseline and dirty state; the invalidation resource
// remains shared so a saved establishment refreshes the open sales forms too.
export default function useWarehouseInvoiceSetting({ warehouseId, companyId, userId, active, value, setValue,
  field, normalize, label }) {
  const client = useQueryClient();
  const baseline = useRef();
  const dirty = useRef(false);
  const [failure, setFailure] = useState("");
  const scope = { userId, companyId, warehouseId, moduleScope: "commercial" };
  const resource = useBusinessResource({
    ...scope, resource: "warehouse-invoice-settings", parameters: [field], enabled: Boolean(warehouseId), active,
    load: async signal => {
      const response = await apiClient.get(`/api/warehouses/${warehouseId}`, { signal, params: { company_id: companyId } });
      return normalize(response.data?.[field]);
    },
  });
  useEffect(() => {
    baseline.current = undefined;
    dirty.current = false;
    setFailure("");
  }, [active, warehouseId, companyId, userId]);
  useEffect(() => {
    if (resource.data === undefined || !active || dirty.current) return;
    baseline.current = resource.data;
    if (value !== resource.data) setValue(resource.data);
  }, [resource.data, active, value, setValue]);
  const conflict = dirty.current && resource.data !== undefined && baseline.current !== undefined && resource.data !== baseline.current;
  return {
    ready: !warehouseId || resource.status === "verified",
    error: failure || (conflict ? "Ce réglage a été modifié depuis un autre poste. Rechargez-le avant d'enregistrer."
      : resource.error ? `Impossible d'actualiser le réglage ${label}. Réessayez avant de le modifier.` : ""),
    onChange(next) { dirty.current = true; setFailure(""); setValue(next); },
    async reload() {
      try {
        const fresh = await resource.refresh({ force: true });
        dirty.current = false; baseline.current = fresh; setValue(fresh); setFailure("");
      } catch (error) { setFailure(`Impossible d'actualiser le réglage ${label}. La saisie est conservée.`); }
    },
    async prepare() {
      if (!warehouseId) return { [field]: normalize(value) };
      if (!dirty.current) return {};
      try {
        const fresh = await resource.refresh({ force: true });
        if (baseline.current === undefined || fresh !== baseline.current) throw new Error("Ce réglage a été modifié depuis un autre poste. Rechargez-le avant d'enregistrer.");
        return { [field]: normalize(value), [`expected_${field}`]: fresh };
      } catch (error) {
        setFailure(error.message || `Impossible de vérifier le réglage ${label}. La saisie est conservée.`);
        throw error;
      }
    },
    saved: () => invalidateBusinessResources(client, scope, "warehouse-invoice-settings"),
  };
}
